import { useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

const useApi = () => {
  const { isAuthenticated, getAccessTokenSilently, sessionExpired } = useAuth();

  const authorizedFetch = useCallback(async (url, method = 'GET', data = null, options = {}) => {
    try {
      if (sessionExpired) {
        const error = new Error('Reauthentication required');
        error.code = 'reauthentication_required';
        throw error;
      }

      let headers = {};
      const isFormData = data instanceof FormData;

      // 🔐 Attach bearer token when authenticated; guest flows still hit token_optional routes without auth
      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        headers['Authorization'] = `Bearer ${token}`;
      }

      // 🧾 Add content type if not FormData and method requires body
      if (method !== 'GET' && !isFormData) {
        headers['Content-Type'] = 'application/json';
      }

      // 🎯 Add space context header for permission context
      if (options.spaceId) {
        headers['X-Space-ID'] = options.spaceId.toString();
      }

      const fetchOptions = {
        method,
        headers,
        ...(data ? { body: isFormData ? data : JSON.stringify(data) } : {}),
      };

      
      const response = await fetch(url, fetchOptions);

      if (response.status === 401 || response.status === 403) {
        console.warn(`🚫 Access denied (status ${response.status}) for ${url}`);
        const errorPayload = await response.json().catch(() => ({}));
        return {
          error: true,
          status: response.status,
          details: errorPayload
        };
      }

      if (!response.ok) {
        const error = new Error(`HTTP error! Status: ${response.status}`);
        try {
          const errorData = await response.json();
          error.response = { data: errorData };
        } catch (e) {
          // If JSON parsing fails, just use the status message
        }
        throw error;
      }
      return await response.json();

    } catch (error) {
      if (error?.code !== 'reauthentication_required') {
        console.error(`🚨 ${method} request failed:`, error);
      }
      throw error;
    }
  }, [isAuthenticated, getAccessTokenSilently, sessionExpired]);

  const get = useCallback((url, spaceId = null) => authorizedFetch(url, 'GET', null, { spaceId }), [authorizedFetch]);
  const post = useCallback((url, data, spaceId = null) => authorizedFetch(url, 'POST', data, { spaceId }), [authorizedFetch]);
  const put = useCallback((url, data, spaceId = null) => authorizedFetch(url, 'PUT', data, { spaceId }), [authorizedFetch]);
  const del = useCallback((url, data, spaceId = null) => authorizedFetch(url, 'DELETE', data, { spaceId }), [authorizedFetch]);

  return useMemo(() => ({ get, post, put, del }), [get, post, put, del]);
};

export default useApi;